<template>
  <span :title="full ? undefined : plain" @click="onClick">
    <!-- Numa linha só, sem espaço entre as tags: cada pedaço encosta no outro como no texto. O texto sem
         marca vai solto, sem tag: duas referências separadas só por um espaço continuam "seguidas" para o
         CSS (`.md-mention + .md-mention`), como no markdown desenhado -->
    <template v-for="(piece, index) in pieces" :key="index"><template v-if="!piece.tag">{{ piece.text }}</template><component v-else :is="piece.tag" :class="piece.classes" :style="piece.style"
               :href="piece.href" :target="piece.external ? '_blank' : undefined"
               :rel="piece.external ? 'noopener' : undefined"><span v-if="piece.prefix" class="md-mention-prefix" v-html="piece.prefix"></span>{{ piece.text }}</component></template>
  </span>
</template>

<script lang="ts">
import {Component, Vue, Prop, Inject, toNative} from 'vue-facing-decorator'
import {markdownExcerpt, markdownSegments, MarkdownSegment} from '@/utils/markdownExcerpt'
import {WORKSPACE} from '@/components/shared/WorkspaceRouteComponent'
import {EnhancedWorkpace} from '@/types/Workspace'
import {followLink, lookPrefix, mentionLook} from '@/utils/markdownMentionView'

/**
 * Um pedaço como se desenha: a tag (nenhuma, no texto sem marca), as classes, e — num link — o endereço e
 * o que vem antes do nome (a bolinha da pessoa, o ícone), no HTML do `lookPrefix`, o mesmo do visualizador.
 */
type Piece = {
  text: string, tag?: 'a' | 'span', classes: string[], style?: string, href?: string,
  prefix?: string, external?: boolean
}

/**
 * Markdown numa linha só, com a fonte de onde está — o tipo e o tamanho vêm do contexto, e só peso,
 * inclinação e risco vêm do markdown (ver `markdownSegments`). Era só do card do kanban, dentro do
 * PropertyValue; a descrição do ambiente na tela inicial pediu o mesmo, e a regra virou um lugar só.
 *
 * O `title` leva o mesmo excerto em texto puro, mais longo.
 *
 * `full`: o texto inteiro, sem o corte — o campo de uma linha da ficha e a descrição de uma linha
 * da página de entrada do ambiente, onde ele é o próprio valor, e não um resumo. Sem o `title`,
 * que repetiria o que já está todo ali.
 *
 * Os links no padrão do app, como no markdown desenhado (pedido do usuário): o item com a régua da cor
 * dele, a coleção com o ícone, a pessoa com a foto (`mentionLook`); o de fora, como link, abrindo outra
 * aba. O clique segue o do visualizador (`followLink`) e não sobe — no card, abriria o item.
 */
@Component({name: 'ontos-markdown-excerpt'})
class MarkdownExcerpt extends Vue {
  @Prop({default: ''}) readonly value!: unknown
  @Prop({type: Boolean, default: false}) readonly full!: boolean
  @Inject({from: WORKSPACE, default: null}) readonly workspace!: EnhancedWorkpace | null

  get segments(): MarkdownSegment[] {
    return this.full ? markdownSegments(this.value, Infinity) : markdownSegments(this.value)
  }

  get pieces(): Piece[] {
    return this.segments.map(segment => {
      const marks = segment.marks.map(mark => `md-${mark}`)
      if (!segment.href) return {text: segment.text, tag: marks.length ? 'span' : undefined, classes: marks}
      const look = this.workspace ? mentionLook(segment.href, this.workspace) : undefined
      //`no-drag`: no card do Kanban, o link não começa o arrasto
      if (!look) return {text: segment.text, tag: 'a', classes: ['md-link', 'no-drag', ...marks], href: segment.href, external: true}
      //A régua, a foto e o ícone só no primeiro pedaço: um link com negrito no meio é mais de um
      return {
        text: segment.text, tag: look.tag, href: look.tag === 'a' ? segment.href : undefined,
        classes: [...(segment.linkStart ? look.classNames : ['md-mention']), 'no-drag', ...marks],
        style: segment.linkStart ? look.style : undefined,
        prefix: segment.linkStart ? lookPrefix(look) || undefined : undefined
      }
    })
  }

  get plain(): string {
    return markdownExcerpt(this.value, 400)
  }

  onClick(event: MouseEvent) {
    followLink(event, this.workspace, href => this.$router.push(href))
  }
}

export default toNative(MarkdownExcerpt)
</script>

<style scoped>
/* As marcas que o excerto aceita: peso, inclinação, risco e um fundo — nenhuma delas aumenta a letra
   nem muda a família da fonte, que é o que mantém a altura do card orçada e a linha da tela inicial
   do tamanho da vizinha. Por isso código embutido NÃO vira monoespaçado: a família mudaria. Os
   títulos (`md-h1`…) não estão aqui: a escala deles é a da forma completa, e mora com ela, no
   markdown.css. As referências (`md-mention`) também moram lá, com o desenho do markdown inteiro. */
.md-bold {
  font-weight: 600;
}

.md-italic {
  font-style: italic;
}

.md-strike {
  text-decoration: line-through;
}

.md-code {
  padding: 0 3px;
  border-radius: 3px;
  background: rgba(0, 0, 0, .06);
}

/* O link de fora, como link: a cor do tema e o sublinhado */
.md-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}
</style>
